"use client"

import { Card, CardContent } from "@/components/ui/card"
import { AnalyticsSummary } from "@/services/analytics-dashboard-service"
import { Eye, Users, Clock, TrendingUp, Globe, Monitor } from 'lucide-react'
import { useTranslation } from "react-i18next"

interface AnalyticsStatsCardsProps {
  analytics: AnalyticsSummary
  className?: string
}

export function AnalyticsStatsCards({ analytics, className }: AnalyticsStatsCardsProps) {
  const formatDuration = (seconds: number): string => {
    if (seconds < 60) return `${seconds}s`
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes}m ${remainingSeconds}s`
  }

  const returnRate = analytics.unique_visitors > 0
    ? Math.round(((analytics.total_views - analytics.unique_visitors) / analytics.unique_visitors) * 100)
    : 0

  const uniqueCountries = new Set(analytics.top_countries?.map(c => c.country)).size
  const uniqueDevices = new Set(analytics.top_devices?.map(d => d.device)).size
  const {t} = useTranslation("analytics")
  const stats = [
    {
      icon: Eye,
      label: t("analytics:card.totalViews"),
      value: analytics.total_views.toLocaleString(),
      color: "text-blue-600 dark:text-blue-400",
      bgColor: "bg-blue-50 dark:bg-blue-900/20",
    },
    {
      icon: Users,
      label: t("analytics:card.uniqueVisitors"),
      value: analytics.unique_visitors.toLocaleString(),
      color: "text-green-600 dark:text-green-400",
      bgColor: "bg-green-50 dark:bg-green-900/20",
    },
    {
      icon: Clock,
      label: t("analytics:card.avgDuration"),
      value: formatDuration(Math.round(analytics.average_duration)),
      color: "text-purple-600 dark:text-purple-400",
      bgColor: "bg-purple-50 dark:bg-purple-900/20",
    },
    {
      icon: TrendingUp,
      label: t("analytics:card.returnRate"),
      value: `${returnRate}%`,
      color: "text-orange-600 dark:text-orange-400",
      bgColor: "bg-orange-50 dark:bg-orange-900/20",
    },
    {
      icon: Globe,
      label: t("analytics:card.countries"),
      value: uniqueCountries.toString(),
      color: "text-indigo-600 dark:text-indigo-400",
      bgColor: "bg-indigo-50 dark:bg-indigo-900/20",
    },
    {
      icon: Monitor,
      label: t("analytics:card.deviceType"),
      value: uniqueDevices.toString(),
      color: "text-pink-600 dark:text-pink-400",
      bgColor: "bg-pink-50 dark:bg-pink-900/20",
    },
  ]

  return (
    <div className={`grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 ${className}`}>
      {stats.map((stat, index) => {
        const Icon = stat.icon
        return (
          <Card key={index} className="overflow-hidden">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${stat.bgColor}`}>
                  <Icon className={`h-4 w-4 ${stat.color}`} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium text-muted-foreground truncate">{stat.label}</p>
                  <p className="text-lg font-bold truncate">{stat.value}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )
      })}
    </div>
  )
}
